import React from 'react'

import { getLocaleParts } from '@constants/format'
import { useFlowField } from '@flows-ui/components/useFlowField'
import { useTranslation } from '@flows-ui/utils/i18n'
import type { TrackFn } from '@flows/configs/analytics/blurTracking'
import { runOnBlurHook } from '@flows/configs/analytics/blurTracking'
import type { AnyCallbackConfig } from '@flows/core/types/dispatchedHookTypes'
import type { PhoneNumber as PhoneNumberProps } from '@flows/core/types/stepContentTypes'
import type { AnySimpleOnSubmitHook, FlowValues } from '@flows/core/types/stepTypes'
import { getTranslatedCountryList } from '@helpers/country'
import PhoneNumberInput from '@ui-kit/components/formComponents/PhoneNumberInput'

interface Props<
  TCallbackConfig extends AnyCallbackConfig,
  TSimpleOnSubmitHook extends AnySimpleOnSubmitHook,
> extends PhoneNumberProps<TCallbackConfig, TSimpleOnSubmitHook> {
  flowValues: FlowValues
  track?: TrackFn
}

export const PhoneNumber = <TProps extends Props<any, any>>(props: TProps & { locale: string }) => {
  const [language] = getLocaleParts(props.locale)
  const { t } = useTranslation()
  const { field, visibleError, label, placeholder } = useFlowField(props, t)
  const countries = React.useMemo(() => {
    return getTranslatedCountryList(language)
  }, [language])

  return (
    <PhoneNumberInput
      defaultCountry={props.defaultCountry}
      optionId="countryId"
      countries={countries}
      testID={props.name}
      label={label}
      placeholder={placeholder}
      name={props.name}
      keyboardType="phone-pad"
      value={field.value}
      styleType={props.styleType ?? 'underlined'}
      onValueChange={field.onChange}
      onBlur={() => {
        field.onBlur()
        runOnBlurHook({ onBlur: props.onBlur, value: field.value, track: props.track })
      }}
      errorMessage={visibleError}
    />
  )
}
